import React, {useEffect, useState} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import RCInput from '../../../utils/RCInput'
import RCSelect from '../../../utils/RCSelect'
import RCMultiSelect from '../../../utils/RCMultiSelect'
import RCDatePickerInput from '../../../utils/RCDatePickerInput'
import RCSwitch from '../../../utils/RCSwitch'
import PhotoUploadField from './shared/PhotoUploadField'
import {RACARequest} from '../../../core/api/Request'
import talentFormSignUpRequest from '../../../core/api/requests/public-signup/talentFormSignUpRequest'
import uploadSignUpFileRequest from '../../../core/api/requests/public-signup/uploadSignUpFileRequest'
import {useDispatch} from 'react-redux'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('El nombre es obligatorio'),
  lastName: Yup.string().required('Los apellidos son obligatorios'),
  email: Yup.string().email('Email no válido').required('El email es obligatorio'),
  phone: Yup.string().required('El teléfono es obligatorio'),
  instagram: Yup.string(),
  city: Yup.string().required('La ciudad es obligatoria'),
  province: Yup.string().required('La provincia es obligatoria'),
  origin: Yup.array().min(1, 'El origen es obligatorio').required('El origen es obligatorio'),
  ethnicity: Yup.array().min(1, 'La etnia es obligatoria').required('La etnia es obligatoria'),
  birthDate: Yup.string().required('La fecha de nacimiento es obligatoria'),
  driverLicence: Yup.array().min(1, 'El carné de conducir es obligatorio').required('El carné de conducir es obligatorio'),
  ownCar: Yup.boolean(),
  gender: Yup.string().required('El género es obligatorio'),
  hairColor: Yup.string().required('El color de pelo es obligatorio'),
  eyesColor: Yup.string().required('El color de ojos es obligatorio'),
  height: Yup.number().required('La altura es obligatoria').positive('Debe ser un número positivo'),
  bust: Yup.number().required('El pecho es obligatorio').positive('Debe ser un número positivo'),
  waist: Yup.number().required('La cintura es obligatoria').positive('Debe ser un número positivo'),
  hip: Yup.number().required('La cadera es obligatoria').positive('Debe ser un número positivo'),
  dressSize: Yup.string().required('La talla de camisa es obligatoria'),
  pantSize: Yup.string().required('La talla de pantalón es obligatoria'),
  shoesSize: Yup.string().required('La talla de zapatos es obligatoria'),
})

const AzafataSignupForm: React.FC = () => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const {agencyId} = useParams<{agencyId: string}>()
  const {executeRecaptcha} = useGoogleReCaptcha()

  const getValuesAsArray = (min: number, max: number): number[] => {
    const values: number[] = []
    for (let i = min; i <= max; i++) {
      values.push(i)
    }
    return values
  }

  const clothingSizeOptions = [
    ...[3, 6, 9, 12, 18, 24].map((m) => ({ key: `${m} meses`, value: `${m} meses` })),
    ...getValuesAsArray(3, 16).map((y) => ({ key: `${y} años`, value: `${y} años` })),
    ...getValuesAsArray(32, 60).map((s) => ({ key: s.toString(), value: s.toString() })),
  ]

  const [futils, setFutils] = useState<any>(null)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // File states
  const [cv, setCv] = useState<File | null>(null)
  const [photoFace, setPhotoFace] = useState<File | null>(null)
  const [photoHalfBody, setPhotoHalfBody] = useState<File | null>(null)
  const [photoFullBody, setPhotoFullBody] = useState<File | null>(null)
  const [photoFree, setPhotoFree] = useState<File | null>(null)

  const [fileErrors, setFileErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    const req = new RACARequest('utils', 'futilsPublic')
    req.makeRequest().then((res: any) => {
      if (res.data) setFutils(res.data)
    })
  }, [])

  const formik = useFormik({
    initialValues: {
      name: '',
      lastName: '',
      email: '',
      phone: '',
      instagram: '',
      city: '',
      province: '',
      origin: [] as string[],
      ethnicity: [] as string[],
      birthDate: '',
      driverLicence: [] as string[],
      ownCar: false,
      languages: [] as string[],
      gender: '',
      hairColor: '',
      eyesColor: '',
      height: '',
      bust: '',
      waist: '',
      hip: '',
      dressSize: '',
      pantSize: '',
      shoesSize: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setSubmitError(null)

      // Validate required files
      const errors: Record<string, string> = {}
      if (!cv) errors.cv = 'El curriculum vitae es obligatorio'
      if (!photoFace) errors.photoFace = 'La foto de cara es obligatoria'
      if (!photoHalfBody) errors.photoHalfBody = 'La foto de medio cuerpo es obligatoria'
      if (!photoFullBody) errors.photoFullBody = 'La foto de cuerpo entero es obligatoria'
      if (!photoFree) errors.photoFree = 'La foto estilo libre es obligatoria'

      if (Object.keys(errors).length > 0) {
        setFileErrors(errors)
        return
      }

      setFileErrors({})

      let recaptchaToken = ''
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('talent_form_signup')
      }

      const payload = {
        formType: 'stewardess',
        recaptchaToken,
        name: values.name,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        instagram: values.instagram,
        city: values.city,
        province: values.province,
        origin: values.origin,
        ethnicity: values.ethnicity,
        birthDate: values.birthDate ? new Date(values.birthDate).toISOString() : '',
        driverLicence: values.driverLicence,
        ownCar: values.ownCar,
        languages: values.languages,
        gender: values.gender,
        hairColor: values.hairColor,
        eyesColor: values.eyesColor,
        height: values.height,
        bust: values.bust,
        waist: values.waist,
        hip: values.hip,
        dressSize: values.dressSize,
        pantSize: values.pantSize,
        shoesSize: values.shoesSize,
        modelTypes: ['Stewardess'],
      }

      try {
        const response = await talentFormSignUpRequest(dispatch, payload, agencyId)

        if (response.statusCode === 200 && response.data?.id) {
          const userId = response.data.id

          if (cv) {
            await uploadSignUpFileRequest(dispatch, userId, cv, 'document', 'cv')
          }
          if (photoFace) {
            await uploadSignUpFileRequest(dispatch, userId, photoFace, 'photo', 'face')
          }
          if (photoHalfBody) {
            await uploadSignUpFileRequest(dispatch, userId, photoHalfBody, 'photo', 'halfBody')
          }
          if (photoFullBody) {
            await uploadSignUpFileRequest(dispatch, userId, photoFullBody, 'photo', 'fullBody')
          }
          if (photoFree) {
            await uploadSignUpFileRequest(dispatch, userId, photoFree, 'photo', 'free')
          }

          setSubmitted(true)
        } else {
          setSubmitError((response as any).message || 'Ha ocurrido un error al enviar el formulario.')
        }
      } catch (error) {
        setSubmitError('Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo.')
      }
    },
  })

  if (submitted) {
    return (
      <div className='container-fluid px-4 px-md-10 py-10' style={{maxWidth: '1400px', margin: '0 auto'}}>
        <div className='card'>
          <div className='card-body p-10 text-center'>
            <h2 className='mb-5'>¡Registro completado!</h2>
            <p className='fs-5 text-muted'>
              Tu formulario se ha enviado correctamente. Nos pondremos en contacto contigo pronto.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='container-fluid px-4 px-md-10 py-10' style={{maxWidth: '1400px', margin: '0 auto'}}>
      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        {/* Header */}
        <div className='card mb-5'>
          <div className='card-header border-0'>
            <div className='card-title m-0'>
              <h3 className='fw-bolder m-0'>Formulario de inscripción - Azafata</h3>
            </div>
          </div>
        </div>

        {/* SECTION: Información Personal */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Información Personal</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Nombre */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Nombre <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('name')}
                  i18n={'PLACEHOLDERS.NAME'}
                  value={formik.values.name || ''}
                />
                {formik.touched.name && formik.errors.name && (
                  <span className='text-danger'>{formik.errors.name}</span>
                )}
              </div>

              {/* Apellidos */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Apellidos <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('lastName')}
                  i18n={'PLACEHOLDERS.LAST_NAME'}
                  value={formik.values.lastName || ''}
                />
                {formik.touched.lastName && formik.errors.lastName && (
                  <span className='text-danger'>{formik.errors.lastName}</span>
                )}
              </div>

              {/* Email */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Email <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('email')}
                  i18n={'PLACEHOLDERS.EMAIL'}
                  value={formik.values.email || ''}
                />
                {formik.touched.email && formik.errors.email && (
                  <span className='text-danger'>{formik.errors.email}</span>
                )}
              </div>

              {/* Teléfono */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Teléfono <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('phone')}
                  i18n={'PLACEHOLDERS.PHONE'}
                  value={formik.values.phone || ''}
                />
                {formik.touched.phone && formik.errors.phone && (
                  <span className='text-danger'>{formik.errors.phone}</span>
                )}
              </div>

              {/* Instagram */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>Instagram</label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('instagram')}
                  i18n={'PLACEHOLDERS.INSTAGRAM'}
                  value={formik.values.instagram || ''}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Datos Personales */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Datos Personales</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Ciudad */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Ciudad <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={(futils?.talents?.contact?.residenceCity || []).map((o: string) => ({
                    key: o,
                    value: intl.formatMessage({id: o}),
                  }))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('city')}
                  i18n={'PLACEHOLDERS.CITY'}
                  value={formik.values.city}
                />
                {formik.touched.city && formik.errors.city && (
                  <span className='text-danger'>{formik.errors.city}</span>
                )}
              </div>

              {/* Provincia */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Provincia <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('province')}
                  i18n={'PLACEHOLDERS.PROVINCE'}
                  value={formik.values.province || ''}
                />
                {formik.touched.province && formik.errors.province && (
                  <span className='text-danger'>{formik.errors.province}</span>
                )}
              </div>

              {/* Origen */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Origen <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={(futils?.talents?.personalInformation?.nationalities || []).map(
                    (o: string) => ({key: o, value: intl.formatMessage({id: o})})
                  )}
                  hideLabel
                  fieldsProps={formik.getFieldProps('origin')}
                  i18n={'PLACEHOLDERS.ORIGIN'}
                  value={formik.values.origin}
                  onChange={(v: string[]) => formik.setFieldValue('origin', v)}
                />
                {formik.touched.origin && formik.errors.origin && (
                  <span className='text-danger'>{String(formik.errors.origin)}</span>
                )}
              </div>

              {/* Etnia */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Etnia <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={(futils?.talents?.personalInformation?.ethnicity || []).map(
                    (o: string) => ({key: o, value: intl.formatMessage({id: o})})
                  )}
                  hideLabel
                  fieldsProps={formik.getFieldProps('ethnicity')}
                  i18n={'PLACEHOLDERS.ETHNICITIES'}
                  value={formik.values.ethnicity}
                  onChange={(v: string[]) => formik.setFieldValue('ethnicity', v)}
                />
                {formik.touched.ethnicity && formik.errors.ethnicity && (
                  <span className='text-danger'>{String(formik.errors.ethnicity)}</span>
                )}
              </div>

              {/* Fecha de nacimiento */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Fecha de nacimiento <span className='text-danger'>*</span>
                </label>
                <RCDatePickerInput
                  allDay
                  hideLabel
                  fieldsProps={formik.getFieldProps('birthDate')}
                  i18n={'PLACEHOLDERS.BIRTH_DATE'}
                  value={formik.values.birthDate}
                />
                {formik.touched.birthDate && formik.errors.birthDate && (
                  <span className='text-danger'>{formik.errors.birthDate}</span>
                )}
              </div>

              {/* Género */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Género <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={(futils?.talents?.personalInformation?.gender || []).map(
                    (o: string) => ({key: o, value: intl.formatMessage({id: o})})
                  )}
                  hideLabel
                  fieldsProps={formik.getFieldProps('gender')}
                  i18n={'PLACEHOLDERS.GENDER'}
                  value={formik.values.gender}
                />
                {formik.touched.gender && formik.errors.gender && (
                  <span className='text-danger'>{formik.errors.gender}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Características Físicas */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Características Físicas</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Pelo */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Pelo <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={(futils?.talents?.physicalCharacteristics?.hairColor || []).map(
                    (o: string) => ({key: o, value: intl.formatMessage({id: o})})
                  )}
                  hideLabel
                  fieldsProps={formik.getFieldProps('hairColor')}
                  i18n={'PLACEHOLDERS.HAIR_COLOR'}
                  value={formik.values.hairColor}
                />
                {formik.touched.hairColor && formik.errors.hairColor && (
                  <span className='text-danger'>{formik.errors.hairColor}</span>
                )}
              </div>

              {/* Ojos */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Ojos <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={(futils?.talents?.physicalCharacteristics?.eyesColor || []).map(
                    (o: string) => ({key: o, value: intl.formatMessage({id: o})})
                  )}
                  hideLabel
                  fieldsProps={formik.getFieldProps('eyesColor')}
                  i18n={'PLACEHOLDERS.EYES_COLOR'}
                  value={formik.values.eyesColor}
                />
                {formik.touched.eyesColor && formik.errors.eyesColor && (
                  <span className='text-danger'>{formik.errors.eyesColor}</span>
                )}
              </div>

              {/* Altura */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Altura (cm) <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={getValuesAsArray(50, 220).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('height')}
                  i18n={'PLACEHOLDERS.HEIGHT'}
                  value={formik.values.height}
                />
                {formik.touched.height && formik.errors.height && (
                  <span className='text-danger'>{formik.errors.height}</span>
                )}
              </div>

              {/* Pecho */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Pecho (cm) <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={getValuesAsArray(20, 200).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('bust')}
                  i18n={'PLACEHOLDERS.BUST'}
                  value={formik.values.bust}
                />
                {formik.touched.bust && formik.errors.bust && (
                  <span className='text-danger'>{formik.errors.bust}</span>
                )}
              </div>

              {/* Cintura */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Cintura (cm) <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={getValuesAsArray(20, 200).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('waist')}
                  i18n={'PLACEHOLDERS.WAIST'}
                  value={formik.values.waist}
                />
                {formik.touched.waist && formik.errors.waist && (
                  <span className='text-danger'>{formik.errors.waist}</span>
                )}
              </div>

              {/* Cadera */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Cadera (cm) <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={getValuesAsArray(20, 200).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('hip')}
                  i18n={'PLACEHOLDERS.HIP'}
                  value={formik.values.hip}
                />
                {formik.touched.hip && formik.errors.hip && (
                  <span className='text-danger'>{formik.errors.hip}</span>
                )}
              </div>

              {/* Talla de camisa */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Talla de camisa <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={clothingSizeOptions}
                  hideLabel
                  fieldsProps={formik.getFieldProps('dressSize')}
                  i18n={'PLACEHOLDERS.DRESS_SIZE'}
                  value={formik.values.dressSize}
                />
                {formik.touched.dressSize && formik.errors.dressSize && (
                  <span className='text-danger'>{formik.errors.dressSize}</span>
                )}
              </div>

              {/* Talla de pantalón */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Talla de pantalón <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={clothingSizeOptions}
                  hideLabel
                  fieldsProps={formik.getFieldProps('pantSize')}
                  i18n={'PLACEHOLDERS.PANT_SIZE'}
                  value={formik.values.pantSize}
                />
                {formik.touched.pantSize && formik.errors.pantSize && (
                  <span className='text-danger'>{formik.errors.pantSize}</span>
                )}
              </div>

              {/* Talla de zapatos */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Talla de zapatos <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={getValuesAsArray(10, 50).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('shoesSize')}
                  i18n={'PLACEHOLDERS.SHOES_SIZE'}
                  value={formik.values.shoesSize}
                />
                {formik.touched.shoesSize && formik.errors.shoesSize && (
                  <span className='text-danger'>{formik.errors.shoesSize}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Otros */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Otros</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Carné de conducir */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Carné de conducir <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={(futils?.talents?.skills?.driverLicence || []).map((o: string) => ({
                    key: o,
                    value: intl.formatMessage({id: o}),
                  }))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('driverLicence')}
                  i18n={'PLACEHOLDERS.DRIVER_LICENSE'}
                  value={formik.values.driverLicence}
                  onChange={(v: string[]) => formik.setFieldValue('driverLicence', v)}
                />
                {formik.touched.driverLicence && formik.errors.driverLicence && (
                  <span className='text-danger'>{String(formik.errors.driverLicence)}</span>
                )}
              </div>

              {/* Coche propio */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <RCSwitch
                  label='Coche propio'
                  fieldsProps={formik.getFieldProps('ownCar')}
                  i18n={'PLACEHOLDERS.OWN_CAR'}
                  value={formik.values.ownCar}
                />
              </div>

              {/* Idiomas */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>Idiomas</label>
                <RCMultiSelect
                  options={(futils?.talents?.language?.languages || []).map(
                    (o: string) => ({key: o, value: intl.formatMessage({id: o})})
                  )}
                  hideLabel
                  fieldsProps={formik.getFieldProps('languages')}
                  i18n={'PLACEHOLDERS.LANGUAGES'}
                  value={formik.values.languages}
                  onChange={(v: string[]) => formik.setFieldValue('languages', v)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Archivos */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Archivos</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Curriculum Vitae */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <PhotoUploadField
                  label='Curriculum Vitae'
                  file={cv}
                  onFileSelect={setCv}
                  accept={{
                    'application/pdf': ['.pdf'],
                    'application/msword': ['.doc'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                  }}
                  required
                />
                {fileErrors.cv && <span className='text-danger d-block mb-4'>{fileErrors.cv}</span>}
              </div>

              {/* Foto de cara */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <PhotoUploadField
                  label='Foto de cara'
                  file={photoFace}
                  onFileSelect={setPhotoFace}
                  required
                />
                {fileErrors.photoFace && (
                  <span className='text-danger d-block mb-4'>{fileErrors.photoFace}</span>
                )}
              </div>

              {/* Foto de medio cuerpo */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <PhotoUploadField
                  label='Foto de medio cuerpo'
                  file={photoHalfBody}
                  onFileSelect={setPhotoHalfBody}
                  required
                />
                {fileErrors.photoHalfBody && (
                  <span className='text-danger d-block mb-4'>{fileErrors.photoHalfBody}</span>
                )}
              </div>

              {/* Foto de cuerpo entero */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <PhotoUploadField
                  label='Foto de cuerpo entero'
                  file={photoFullBody}
                  onFileSelect={setPhotoFullBody}
                  required
                />
                {fileErrors.photoFullBody && (
                  <span className='text-danger d-block mb-4'>{fileErrors.photoFullBody}</span>
                )}
              </div>

              {/* Foto estilo libre */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <PhotoUploadField
                  label='Foto estilo libre'
                  file={photoFree}
                  onFileSelect={setPhotoFree}
                  required
                />
                {fileErrors.photoFree && (
                  <span className='text-danger d-block mb-4'>{fileErrors.photoFree}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {submitError && (
          <div className='alert alert-danger mt-4'>
            <span>{submitError}</span>
          </div>
        )}

        <div className='d-flex justify-content-end py-6'>
          <button type='submit' className='btn btn-primary' disabled={formik.isSubmitting}>
            {formik.isSubmitting ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </form>
    </div>
  )
}

export {AzafataSignupForm}
